import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../Logo';
import API from '../../utils/api';

const NAV = [
  { to: '/admin',            icon: 'bi-speedometer2',    label: 'Dashboard',      exact: true },
  { to: '/admin/users',      icon: 'bi-people',           label: 'Users' },
  { to: '/admin/stories',    icon: 'bi-book',             label: 'Stories' },
  { to: '/admin/support',    icon: 'bi-chat-dots',        label: 'Support' },
  { to: '/admin/gifts',      icon: 'bi-gift',             label: 'Gift Orders' },
  { to: '/admin/revenue',    icon: 'bi-bar-chart-line',   label: 'Revenue' },
  { to: '/admin/voices',     icon: 'bi-mic',              label: 'Voice Profiles' },
  { to: '/admin/series',     icon: 'bi-collection-play',  label: 'Story Series' },
  { to: '/admin/community',  icon: 'bi-globe2',           label: 'Community' },
  { to: '/admin/reports',    icon: 'bi-flag',             label: 'Reports' },
  { to: '/admin/broadcast',  icon: 'bi-megaphone',        label: 'Broadcast Email' },
  { to: '/admin/blog',       icon: 'bi-pencil-square',    label: 'Blog' },
  { to: '/admin/affiliates', icon: 'bi-megaphone-fill',   label: 'Affiliates' },
  { to: '/admin/logs',       icon: 'bi-journal-text',     label: 'Activity Logs' },
  { to: '/admin/settings',  icon: 'bi-sliders',           label: 'Site Settings' },
];

const AdminLayout = ({ children, title }) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [pendingAffiliates, setPendingAffiliates] = useState(0);

  useEffect(() => {
    API.get('/admin/affiliates/pending-count')
      .then(r => setPendingAffiliates(r.data.count || 0))
      .catch(() => {});
  }, [location.pathname]); // refresh on every page change

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f1fb' }}>

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <>
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 199 }}
          />
        )}

        <aside style={{
          width: '240px',
          flexShrink: 0,
          background: '#1a0533',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: sidebarOpen ? 0 : undefined,
          zIndex: 200,
          transition: 'left 0.25s',
          // On desktop always visible; on mobile slide in/out
        }} className="d-none d-lg-flex flex-column">
          <SidebarContent nav={NAV} isActive={isActive} user={user} onLogout={handleLogout} pendingAffiliates={pendingAffiliates} />
        </aside>

        {/* Mobile sidebar */}
        <aside style={{
          width: '240px',
          background: '#1a0533',
          position: 'fixed',
          top: 0,
          left: sidebarOpen ? 0 : '-260px',
          bottom: 0,
          zIndex: 200,
          transition: 'left 0.25s',
          display: 'flex',
          flexDirection: 'column',
        }} className="d-lg-none">
          <SidebarContent nav={NAV} isActive={isActive} user={user} onLogout={handleLogout} onClose={() => setSidebarOpen(false)} pendingAffiliates={pendingAffiliates} />
        </aside>
      </>

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <div style={{ marginLeft: 0, flex: 1, display: 'flex', flexDirection: 'column' }} className="ms-lg-0" id="admin-main">
        <div style={{ marginLeft: '240px' }} className="d-none d-lg-block" id="lg-spacer" />

        {/* Topbar */}
        <header style={{
          background: 'var(--card-bg)',
          borderBottom: '1px solid var(--border-purple)',
          padding: '0 24px',
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          marginLeft: 0,
        }} className="ms-lg-admin">
          {/* Mobile hamburger */}
          <button
            className="btn btn-sm d-lg-none border-0"
            onClick={() => setSidebarOpen(o => !o)}
            style={{ color: '#6f42c1', padding: '4px 8px' }}
          >
            <i className="bi bi-list fs-4"></i>
          </button>

          <h6 className="mb-0 fw-bold d-none d-lg-block" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '18px' }}>
            {title || 'Admin Panel'}
          </h6>

          <div className="d-flex align-items-center gap-3">
            <Link to="/dashboard" className="btn btn-sm btn-outline-secondary rounded-pill" style={{ fontSize: '12px' }}>
              <i className="bi bi-arrow-left me-1"></i>Customer View
            </Link>
            <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
              style={{ width: '32px', height: '32px', background: '#6f42c1', fontSize: '13px', flexShrink: 0 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: '28px 24px', maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
          {children}
        </main>
      </div>

      {/* Offset for fixed sidebar on large screens */}
      <style>{`
        @media (min-width: 992px) {
          #admin-main { margin-left: 240px !important; }
          #lg-spacer  { display: none !important; }
          .ms-lg-admin { margin-left: 0 !important; }
        }
      `}</style>
    </div>
  );
};

const SidebarContent = ({ nav, isActive, user, onLogout, onClose, pendingAffiliates }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    {/* Brand */}
    <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/admin" style={{ textDecoration: 'none' }}>
          <Logo size={32} variant="wordmark-light" />
        </Link>
        {onClose && (
          <button onClick={onClose} className="btn btn-sm border-0 p-0" style={{ color: '#9b7dd4' }}>
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>
    </div>

    {/* Nav */}
    <nav style={{ flex: 1, padding: '12px 12px' }}>
      {nav.map(item => (
        <Link
          key={item.to}
          to={item.to}
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '9px 12px',
            borderRadius: '10px',
            marginBottom: '2px',
            textDecoration: 'none',
            color: isActive(item) ? '#fff' : '#9b7dd4',
            background: isActive(item) ? 'rgba(111,66,193,0.45)' : 'transparent',
            fontWeight: isActive(item) ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.15s',
          }}
        >
          <i className={`bi ${item.icon}`} style={{ fontSize: '16px', width: '18px', textAlign: 'center' }}></i>
          <span style={{ flex: 1 }}>{item.label}</span>
          {item.to === '/admin/affiliates' && pendingAffiliates > 0 && (
            <span style={{
              background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 700,
              borderRadius: '50px', padding: '1px 6px', minWidth: '18px', textAlign: 'center',
            }}>
              {pendingAffiliates}
            </span>
          )}
        </Link>
      ))}
    </nav>

    {/* User + logout */}
    <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
          style={{ width: '30px', height: '30px', background: '#6f42c1', fontSize: '12px', flexShrink: 0 }}>
          {user?.name?.charAt(0).toUpperCase()}
        </div>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ color: '#fff', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
          <div style={{ color: '#6b5a8a', fontSize: '11px' }}>Administrator</div>
        </div>
      </div>
      <button
        onClick={onLogout}
        className="btn btn-sm w-100"
        style={{ background: 'rgba(255,255,255,0.06)', color: '#9b7dd4', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '13px' }}
      >
        <i className="bi bi-box-arrow-right me-2"></i>Sign out
      </button>
    </div>
  </div>
);

export default AdminLayout;
